import type { Geometry, Position } from 'geojson'

/**
 * An outline with its needle spikes taken off, for the one mark that cannot
 * carry them: the amber ribbon round the selected place.
 *
 * Some boundaries in the source data are wrong in a particular way. Los Olivos
 * District's north edge is 23 vertices of zigzag, each spike a kilometre or two
 * long and two hundred metres wide at its base; Napa Valley has a run of them
 * along Lake Berryessa and one eighteen kilometres long. They are digitising
 * errors, they survive simplification (the tip of a spike is far from the line
 * through its base, which is all Douglas-Peucker asks), and nothing short of
 * re-importing the geometry removes them from the atlas.
 *
 * As a 1 px claret hairline a spike is a scratch. As the selection it is a
 * 5 px opaque amber ribbon, inset, with a paper casing — along both sides of a
 * needle narrower than the ribbon, so the ribbon folds over itself and the
 * loudest thing on the page is a data error. So the SELECTION is drawn from a
 * copy with the spikes taken off, and so is the hole in the veil, so the two
 * agree. The appellation's own claret edge is switched off while it is
 * selected (region_layer.ts), so nothing is left drawing the spike.
 *
 * WHAT COUNTS AS A SPIKE. A vertex where the outline turns back on itself
 * within `SPIKE_DEGREES`, and whose removal changes the shape by less than
 * `SPIKE_AREA_SHARE` of its area. The second test is what tells a needle from
 * a corner: a three-cornered appellation has sharp vertices too, and each of
 * them is a third of it. The sharpest goes first and the rest are measured
 * again, because taking the tip off one spike changes the angle at its base.
 *
 * It is not a fix for the data and does not pretend to be. A zigzag comes out
 * as a few shallow teeth rather than a straight line, the unselected hairline
 * still shows every spike, and where the spike was real — Santa Cruz
 * Mountains is drawn along a contour — the selection is a little smoother than
 * the law. The server's simplifier is where this belongs, at import.
 *
 * WHAT IT COSTS. Once per place selected, never per frame: the answer is kept
 * against the geometry object it was worked out for, and a pan that re-runs
 * the selection hands the same object back. Each spike taken off is another
 * pass over the ring, so it grows with the square of a spiky outline: timed
 * in Node on a busy machine, 0.02 ms for a hundred vertices, 0.3 ms for four
 * hundred and 3.5 ms for fifteen hundred with one vertex in seventeen a spike
 * — which is more spikes than any outline in the atlas has.
 */

// How sharp a turn is a spike. At 30 degrees Los Olivos District loses fifteen
// of its twenty-three and what is left reads as an edge; at 20 the ribbon
// still folds over the survivors.
export const SPIKE_DEGREES = 30

// And how much of the shape a spike may be. Los Olivos' are a thousandth each
// and Napa Valley's longest is two thousandths.
export const SPIKE_AREA_SHARE = 0.02

// A ring is never cut below a quadrilateral.
const FEWEST_VERTICES = 4

const COS_SPIKE = Math.cos((SPIKE_DEGREES * Math.PI) / 180)

const kept = new WeakMap<Geometry, Geometry>()

function ringArea(points: readonly Position[], stretch: number) {
  let twice = 0

  for (let index = 0; index < points.length; index++) {
    const from = points[index]
    const to = points[(index + 1) % points.length]
    twice += (from[0] * to[1] - to[0] * from[1]) * stretch
  }

  return Math.abs(twice) / 2
}

// One closed ring. Returns the ring it was given when it has no spike.
function despikedRing(ring: Position[]): Position[] {
  if (ring.length - 1 <= FEWEST_VERTICES) return ring

  // Open, to work on: the last position repeats the first.
  const points = ring.slice(0, -1)
  // Degrees of longitude are shorter than degrees of latitude by this much;
  // an angle measured without it is wrong by the latitude.
  const stretch = Math.cos((points[0][1] * Math.PI) / 180)
  const most = ringArea(points, stretch) * SPIKE_AREA_SHARE

  for (;;) {
    if (points.length <= FEWEST_VERTICES) break

    let sharpest = -1
    let sharpestCos = COS_SPIKE

    for (let index = 0; index < points.length; index++) {
      const before = points[(index + points.length - 1) % points.length]
      const at = points[index]
      const after = points[(index + 1) % points.length]
      const ux = (before[0] - at[0]) * stretch
      const uy = before[1] - at[1]
      const vx = (after[0] - at[0]) * stretch
      const vy = after[1] - at[1]
      const lengths = Math.hypot(ux, uy) * Math.hypot(vx, vy)
      if (lengths === 0) continue

      const cos = (ux * vx + uy * vy) / lengths
      if (cos <= sharpestCos) continue
      if (Math.abs(ux * vy - uy * vx) / 2 > most) continue

      sharpest = index
      sharpestCos = cos
    }

    if (sharpest < 0) break
    points.splice(sharpest, 1)
  }

  return points.length === ring.length - 1 ? ring : [...points, points[0]]
}

function despikedPolygon(rings: Position[][]): Position[][] {
  let changed = false
  const out = rings.map((ring) => {
    const clean = despikedRing(ring)
    if (clean !== ring) changed = true

    return clean
  })

  return changed ? out : rings
}

/**
 * The geometry with its spikes taken off: the same object when it has none (or
 * is not an area), a new one when it has. Asked twice of one object, it is
 * answered from memory.
 */
export function despiked(geometry: Geometry): Geometry {
  const known = kept.get(geometry)
  if (known) return known

  let clean: Geometry = geometry

  if (geometry.type === 'Polygon') {
    const rings = despikedPolygon(geometry.coordinates)
    if (rings !== geometry.coordinates) clean = { type: 'Polygon', coordinates: rings }
  } else if (geometry.type === 'MultiPolygon') {
    let changed = false
    const polygons = geometry.coordinates.map((rings) => {
      const out = despikedPolygon(rings)
      if (out !== rings) changed = true

      return out
    })
    if (changed) clean = { type: 'MultiPolygon', coordinates: polygons }
  }

  kept.set(geometry, clean)

  return clean
}
